// Prompts a long-lived tab to refresh before its chunks stop resolving — the
// polite half of the pair with utils/appReload.js. See CLAUDE.md (CPRD-5916).

import { toast } from '@/composables/useAlertToast';

export const VERSION_POLL_INTERVAL_MS = 5 * 60 * 1000;
// Floor on the visibility-driven check only: alt-tabbing back and forth would
// otherwise send a fetch per switch, all of them redundant with the interval.
export const MIN_VISIBILITY_CHECK_GAP_MS = 30 * 1000;
export const VERSION_FILE = 'version.json';
export const UPDATE_TOAST_ID = 'app-update-available';
export const UPDATE_TOAST_MESSAGE = 'A new version is available, refresh';

// Which build we already prompted about; dies with the tab, like every other
// per-session key here.
const PROMPTED_KEY = 'auxo:update-prompted';

function normalize(value) {
  return String(value ?? '').trim();
}

export function runningBuild() {
  return {
    version: normalize(import.meta.env.VITE_APP_VERSION),
    commit: normalize(import.meta.env.VITE_APP_COMMIT),
  };
}

/** Comparable at all — a local `npm run dev` build carries neither field. */
export function isIdentifiableBuild(build) {
  return Boolean(normalize(build?.version) || normalize(build?.commit));
}

/**
 * True only when the deployed build is demonstrably not the running one: a
 * field decides only when both sides carry it, so nothing comparable means no
 * prompt. Commit first, since a rebuild can reuse a tag but not a sha.
 */
export function isNewBuild(running, deployed) {
  if (!running || !deployed) return false;

  for (const field of ['commit', 'version']) {
    const mine = normalize(running[field]);
    const theirs = normalize(deployed[field]);
    if (!mine || !theirs) continue;
    return mine !== theirs;
  }

  return false;
}

export function buildIdentity(build) {
  return [normalize(build?.version), normalize(build?.commit)]
    .filter(Boolean)
    .join('@');
}

function alreadyPrompted(identity) {
  try {
    return sessionStorage.getItem(PROMPTED_KEY) === identity;
  } catch {
    return false;
  }
}

function rememberPrompt(identity) {
  try {
    sessionStorage.setItem(PROMPTED_KEY, identity);
  } catch {
    // ignore — unavailable storage only costs a repeated prompt
  }
}

// BASE_URL-relative; the edge resolves it to the tab's own slot (CPRD-5918).
export function versionFileUrl(base = import.meta.env.BASE_URL || '/') {
  return `${base.replace(/\/+$/, '')}/${VERSION_FILE}`;
}

/** Null whenever it cannot be read: offline, mid-deploy, older build. */
export async function fetchDeployedBuild() {
  try {
    const response = await fetch(versionFileUrl(), {
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (!response.ok) return null;

    const payload = await response.json();
    return {
      version: normalize(payload?.version),
      commit: normalize(payload?.commit),
    };
  } catch (error) {
    console.debug('Version check failed:', error);
    return null;
  }
}

/**
 * Polls version.json and toasts once per new build. The prompt is recorded when
 * shown, so a dismissed toast cannot come back for the same build.
 */
export function useVersionCheck({
  intervalMs = VERSION_POLL_INTERVAL_MS,
} = {}) {
  let timer;
  let onVisibilityChange;
  let lastCheckedAt = 0;

  async function check() {
    lastCheckedAt = Date.now();

    const running = runningBuild();
    if (!isIdentifiableBuild(running)) return false;

    const deployed = await fetchDeployedBuild();
    if (!isNewBuild(running, deployed)) return false;

    const identity = buildIdentity(deployed);
    if (alreadyPrompted(identity)) return false;
    rememberPrompt(identity);

    toast.info(UPDATE_TOAST_MESSAGE, {
      id: UPDATE_TOAST_ID,
      duration: Infinity,
    });
    return true;
  }

  function start() {
    // Nothing to compare against outside a deployed build.
    if (!isIdentifiableBuild(runningBuild())) return;
    if (timer) return;

    timer = setInterval(check, intervalMs);

    // A backgrounded tab's timers are throttled, and coming back is when the
    // answer matters — unless the interval just asked.
    onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastCheckedAt < MIN_VISIBILITY_CHECK_GAP_MS) return;
      check();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    check();
  }

  function stop() {
    clearInterval(timer);
    timer = undefined;
    if (onVisibilityChange) {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      onVisibilityChange = undefined;
    }
  }

  return { start, stop, check };
}
